import * as monacoEditor from 'monaco-editor';

import { jsoncEditsToMonacoEdits } from 'src/app/graph/explorerPage/helpers/monacoHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { EditorBottomTab } from '../../helpers/EditorBottomTab';

import { Deletion } from './getDeletion';
import { Insertion } from './getInsertion';

export interface EditAction {
  type:
    | 'insertArgument'
    | 'deleteArgument'
    | 'format'
    | 'inlineVariables'
    | 'extractVariables'
    | 'inlineFragment'
    | 'extractFragment'
    | 'wrapWithDeferFragment'
    | (Deletion | Insertion)['type'];
  variableEdits: Insertion['variableEdits'];
  edits?: Insertion['edits'];
  endCursorOffset?: Insertion['endCursorOffset'];
}

/**
 * executes the operation and variable edits in the monaco editors.
 * will set focus the operation editor if `edits` is passed
 * will open editorBottomTab if variableEdits passed
 *
 * @param action.editor monaco edits
 * @param action.variableEdits jsoncParser edits, we need to apply these in sequence because each edit relys on the previous one
 */
export function executeEdits(
  action: EditAction,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  variablesEditor?: monacoEditor.editor.IStandaloneCodeEditor,
  setActiveEditorBottomTab?: (tab: EditorBottomTab) => void,
  selectEditedRanges: boolean = false,
) {
  const model = editor.getModel();
  const variableModel = variablesEditor?.getModel();

  if (!model) {
    // Shouldn't be able to get here, models are checked at call site
    throw new Error('missing model');
  }
  if (action.edits) {
    let selections: monacoEditor.Selection[] | undefined;
    editor.executeEdits(action.type, action.edits, (undoEdits) => {
      if (action.type === 'deleteDocNode' || !isMinLengthArray(1, undoEdits)) {
        return null;
      }
      const writePosition = !action.endCursorOffset
        ? undoEdits[0].range.getStartPosition()
        : model.getPositionAt(action.endCursorOffset);
      const selection = monacoEditor.Selection.fromPositions(
        writePosition,
        writePosition,
      );
      try {
        // Calling setSelections inside executeEdits does not work, keep these
        // till after.
        selections = undoEdits.flatMap((edit) =>
          edit.range.isEmpty()
            ? []
            : monacoEditor.Selection.fromPositions(
                edit.range.getStartPosition(),
                edit.range.getEndPosition(),
              ),
        );
        editor.revealRangeInCenterIfOutsideViewport(selection);
      } catch {
        // TODO (jason) come back to this. Calling reveal* on a line outside
        // of the previous bounds after an insertion may crash if it tries to
        // check the line visibility before the line gets updated. I tried a
        // few things here to force monaco to process the model update but
        // couldn't get anything to work. This is fine in most cases to
        // ignore, the one possibly annoying thing would be if someone added a
        // large insertion for which the final field was out of scroll bounds
        // it would not get centered. This seems unlikely to happen often
      }
      return [selection];
    });
    if (selectEditedRanges && selections) {
      editor.setSelections(selections);
    }
  }
  // jsonc-parser can only delete 1 key at a time so we have to so we have to
  // loop over edit groups returned from getDeletion.
  if (variablesEditor && variableModel && action.variableEdits.length) {
    action.variableEdits.forEach((editGroup, i, { length }) => {
      variablesEditor.executeEdits(
        action.type,
        jsoncEditsToMonacoEdits(editGroup, variableModel),
        // only update view positon on the last edit
        i === length - 1
          ? (undoEdits) => {
              if (isMinLengthArray(1, undoEdits)) {
                const startPosition = undoEdits[0].range.getStartPosition();
                const selection = monacoEditor.Selection.fromPositions(
                  startPosition,
                  startPosition,
                );
                try {
                  variablesEditor.revealRangeInCenterIfOutsideViewport(
                    selection,
                  );
                } catch {
                  // TODO (jason) come back to this. Calling reveal* on a line outside
                  // of the previous bounds after an insertion may crash if it tries to
                  // check the line visibility before the line gets updated. I tried a
                  // few things here to force monaco to process the model update but
                  // couldn't get anything to work. This is fine in most cases to
                  // ignore, the one possibly annoying thing would be if someone added a
                  // large insertion for which the final field was out of scroll bounds
                  // it would not get centered. This seems unlikely to happen often
                }
              }
              return null;
            }
          : undefined,
      );
    });
    if (action.type === 'insertArgument' || action.type === 'insertDocNode') {
      setActiveEditorBottomTab?.('Variables');
    }
  }
  if (action.edits) editor.focus();
}
